/**
 * Pure helpers for the per-pane sidepanel layout introduced by
 * `bmaps-sidepanel-independent-columns` (2026-05-28).
 *
 * These functions have no React, no Redux, no DOM dependencies — they
 * operate on plain values so they're unit-testable in isolation.
 *
 * See `labwork/studies/bmaps-sidepanel-independent-columns/approach.md`
 * §§ D1, D6, D7, D8 for the decisions these helpers encode.
 *
 * @module ui/info_display/sidePanelHelpers
 */

/**
 * Canonical list of stackable pane ids known at the time the migration
 * helper is called. This default keeps existing call sites working without
 * having to thread the list through every layer; new stackable panes
 * SHOULD pass an explicit list so the legacy migration seeds them too.
 *
 * Synchronized with the `stackable` prop on children of
 * `<SidePanelLayout>` in `proto/src/ui/BMapsPage.jsx`.
 */
export const DEFAULT_STACKABLE_PANE_IDS = ['help', 'workflow'];

/**
 * Migrate a persisted `sidePanels.<side>` value to the per-pane
 * shape used by this study.
 *
 * Three input shapes are handled:
 *
 *   1. **New shape** — already has a `panes` map. Returned as-is.
 *   2. **Legacy shape** — has a single `sidePanelWidth` (and optionally
 *      `minimized`). The width is seeded onto every known stackable pane
 *      so the user's previously-chosen width survives the upgrade for
 *      both panes; the user can then drag each independently from there.
 *   3. **First-time / empty** — no persisted state. Returns an empty
 *      object; consumers fall back to canonical defaults per pane.
 *
 * Decision D8 in `approach.md`. The legacy `sidePanelWidth` field is
 * intentionally NOT consumed here — callers can leave it in place for
 * one release as a safety net before a follow-up cleanup PR drops it.
 *
 * @param {*} persisted - Raw `sidePanels.<side>` value (may be
 *   null/undefined/non-object for defensive handling).
 * @param {string[]} [knownPaneIds=DEFAULT_STACKABLE_PANE_IDS] - Pane ids
 *   to seed from the legacy single-width field. Pass an explicit list
 *   when a third stackable pane joins the layout.
 * @returns {Object<string, {width: number, minimized: boolean}>} Per-pane
 *   width + minimize map. May be empty.
 */
export function migratePersistedShape(persisted, knownPaneIds = DEFAULT_STACKABLE_PANE_IDS) {
    // Defensive: anything that isn't an object yields a first-time-user map.
    if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
        return {};
    }

    // New shape: passthrough. Trust the persisted map but defensively
    // copy so callers can't mutate persisted state through the return.
    if (persisted.panes && typeof persisted.panes === 'object' && !Array.isArray(persisted.panes)) {
        const out = {};
        for (const [paneId, paneState] of Object.entries(persisted.panes)) {
            if (paneState && typeof paneState === 'object') {
                out[paneId] = {
                    width: typeof paneState.width === 'number' ? paneState.width : null,
                    minimized: !!paneState.minimized,
                };
            }
        }
        return out;
    }

    // Legacy shape: seed every known stackable pane from the single width.
    if (typeof persisted.sidePanelWidth === 'number' && persisted.sidePanelWidth > 0) {
        const seed = persisted.sidePanelWidth;
        const minimized = !!persisted.minimized;
        const out = {};
        for (const paneId of knownPaneIds) {
            out[paneId] = { width: seed, minimized };
        }
        return out;
    }

    // First-time user / unrecognized shape: empty map.
    return {};
}

// ─── Width-budget constants (D6 — REVISED 2026-06-01) ────────────────
//
// 2026-06-01: per Jess during the indep-cols smoke walk, the per-pane
// app-side cap was removed. The original 0.50 / 0.60 fractions were
// surfacing as "the panel won't go past X" on her viewport, which read
// to her as an arbitrary limit. Both fractions are now 1.0 (effectively
// "no app-side cap"). The cursor math in SidePanelResizeHandle
// (`window.innerWidth - clientX - HANDLE_OFFSET`) is the only effective
// upper bound, which is fine: cursor can't leave the viewport, so the
// panel can't grow past the viewport either. In the multi-pane case,
// the total budget still prevents pane B from overlapping pane A
// because othersSum is subtracted from the total budget.

/**
 * Per-pane cap as a fraction of viewport. Set to 1.0 → no app-side cap;
 * the cursor math in `SidePanelResizeHandle.computeNewWidth` bounds the
 * pane to `viewport - HANDLE_OFFSET` naturally.
 */
export const PER_PANE_MAX_FRACTION = 1.0;

/**
 * Combined cap across all open stackable panes as a fraction of viewport.
 * Set to 1.0 → the combined width may consume the full viewport. With
 * two panes open, `clampPaneToBudget` still subtracts the other pane's
 * width from the budget so they don't overlap.
 */
export const TOTAL_OPEN_MAX_FRACTION = 1.0;

/**
 * Clamp a proposed pane width against the per-pane individual maximum
 * AND the total-width budget across all open stackable panes.
 *
 * The dragged pane is the one whose width is being proposed; sibling
 * panes are NOT auto-shrunk to make room. If the proposed width would
 * push the total over budget, the dragged pane is soft-clamped at
 * `total_cap - sum(otherOpenPaneWidths)`. If the per-pane max alone
 * would be exceeded, the dragged pane is clamped to that max.
 *
 * The returned width may be smaller than `proposed` but never larger.
 *
 * @param {number} proposed - Proposed width in CSS pixels.
 * @param {number[]} otherOpenPaneWidths - Widths of other currently-open
 *   stackable panes (excluding the one being dragged), in CSS pixels.
 *   Empty array for the single-pane case.
 * @param {number} viewport - Current viewport width in CSS pixels.
 *   Typically `window.innerWidth`.
 * @returns {number} Clamped width. Always ≤ proposed, ≥ 0.
 */
export function clampPaneToBudget(proposed, otherOpenPaneWidths, viewport) {
    if (typeof proposed !== 'number' || !Number.isFinite(proposed) || proposed < 0) {
        return 0;
    }
    if (typeof viewport !== 'number' || !Number.isFinite(viewport) || viewport <= 0) {
        return proposed; // no viewport info — pass through, defer clamping to caller
    }
    const others = Array.isArray(otherOpenPaneWidths) ? otherOpenPaneWidths : [];
    const othersSum = others.reduce((acc, w) => acc + (typeof w === 'number' && w > 0 ? w : 0), 0);

    const perPaneMax = Math.floor(PER_PANE_MAX_FRACTION * viewport);
    const totalMax = Math.floor(TOTAL_OPEN_MAX_FRACTION * viewport);
    const totalBudgetForThisPane = Math.max(0, totalMax - othersSum);

    // The dragged pane can't exceed its individual max OR what's left of the total budget.
    return Math.min(proposed, perPaneMax, totalBudgetForThisPane);
}

// ─── Ported from the fork line (computeRailSnapTrigger) ─────────────────────────────────
// The suite on this branch exercises computeRailSnapTrigger; it exists on the fork and not here.
// Brought across verbatim, with the same-file helpers it calls.

/**
 * Decide whether the right SidePanel column should snap into rail mode.
 *
 * Per D7: rail is column-level state. The trigger is the COMBINED width
 * of all open stackable panes dropping below the rail threshold —
 * matching the user's mental model of "minimize the whole right area
 * as one action," not "per-pane independent minimize." A single slim
 * pane co-existing with a normal-width pane keeps the column active.
 *
 * Closed panes (those NOT included in `openPaneWidths`) contribute zero
 * to the combined width.
 *
 * When zero panes are open, returns `false` — there is nothing to
 * minimize, the column simply has no visible content.
 *
 * @param {number[]} openPaneWidths - Widths (CSS px) of currently-open
 *   stackable panes. May be empty.
 * @param {number} threshold - The combined-width threshold below which
 *   rail mode triggers. Typically `MINIMIZE_THRESHOLD_REM * rootFontPx`
 *   (18rem ≈ 288 px on a 16 px root).
 * @returns {boolean} `true` if rail mode should activate.
 */
export function computeRailSnapTrigger(openPaneWidths, threshold) {
    if (!Array.isArray(openPaneWidths) || openPaneWidths.length === 0) return false;
    if (typeof threshold !== 'number' || !Number.isFinite(threshold) || threshold <= 0) {
        return false;
    }
    const combined = openPaneWidths.reduce(
        (acc, w) => acc + (typeof w === 'number' && w > 0 ? w : 0),
        0,
    );
    // all open panes have zero/invalid width -- a caller bug, not a rail condition
    if (combined <= 0) return false;
    return combined < threshold;
}
